import { Button, Col, Image, Modal, message, Popover, Radio, Row, Select } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Webcam from 'react-webcam';

const ExamWebcamModal = ({
  visible,
  closeModal,
  capture,
  webcamRef,
  imageSrc,
  resetImage,
  videoConstraints,
  countrySettings,
  countryCode,
  addToReport,
  setAddToReport,
}) => {
  const { t } = useTranslation();
  const [deviceId, setDeviceId] = useState('');
  const [devices, setDevices] = useState([]);

  const hasImage = imageSrc && !imageSrc.includes('missing');

  const handleDevices = useCallback(
    mediaDevices => {
      const filteredDevices = mediaDevices.filter(({ kind }) => kind === 'videoinput');
      setDevices(filteredDevices);
      if (filteredDevices.length > 0) {
        setDeviceId(filteredDevices[0].deviceId);
      }
    }, [setDevices]);

  const changeDevice = (id) => {
    setDeviceId(id);
  };

  useEffect(() => {
    let enumerationStream;
    const getDevices = async () => {
      try {
        enumerationStream = await navigator?.mediaDevices.getUserMedia({ video: true });
        const allDevices = await navigator?.mediaDevices.enumerateDevices();
        handleDevices(allDevices);
      } catch (err) {
        console.error('Error enumerating devices or getting permissions:', err);
        message.error({
          content: 'Error al acceder a la cámara',
          style: {
            marginTop: '40vh',
          },
        });
        setDevices([]);
      }
    };
    getDevices();
    return () => {
      if (enumerationStream) {
        enumerationStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [handleDevices]);

  const showAddToReport = countrySettings?.uploadDocumentsModal?.[countryCode];

  const TooltipContent = () => (
    <div className='modal-tooltip'>
      <div className='pb-10'><strong>{t('examPage.uploadDocumentModal.tooltipTitle')}</strong></div>
      <div>{t('examPage.uploadDocumentModal.tooltipText')}</div>
    </div>
  );

  return (
    <Modal
      visible={visible}
      centered
      footer={false}
      closable
      onCancel={closeModal}
      responsive
      destroyOnClose
      className='webcam-modal webcam-modal--exam'
      width={650}
    >
      <Row align='middle' justify='center' className='pt-30 pb-30' gutter={[0, 30]}>
        <Col span={20}>
          <h2 className='webcam-modal-title'>{t('examPage.webcamModal.title')}</h2>
          <p className='webcam-modal-subtitle'>{t('examPage.webcamModal.subtitle')}</p>
        </Col>
        {!hasImage && (
          <Col span={20}>
            Dispositivo
            <Select
              className='cmt-select mt-10'
              value={deviceId}
              onChange={changeDevice}
              placeholder='Seleccione una cámara'
              size="middle"
              options={devices?.map((device) => ({ label: device.label, value: device.deviceId }))}
            />
          </Col>
        )}
        <Col span={20}>
          <div className="webcam-media-container">
            {hasImage ? (
              <Image
                src={imageSrc}
                alt='exam photo'
                style={{ width: '100%' }}
              />
            ) : (
              <Webcam
                key={deviceId}
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/png"
                width="100%"
                videoConstraints={{ ...videoConstraints, ...(deviceId ? { deviceId: { exact: deviceId } } : {}) }}
              />
            )}
          </div>
        </Col>
        {hasImage && showAddToReport && (
          <Col span={20}>
            <Row>
              <Col span={24}>
                <Row className='mb-10'>
                  <div className='title-text webcam-modal-instruction'>{t('examPage.webcamModal.radioButtonInstruction')} <strong>{t('examPage.webcamModal.radioButtonInstructionBold')}</strong></div>
                </Row>
                <Row className='mb-10'>
                  <strong>{t('examPage.webcamModal.radioButtonTitle')}</strong>
                </Row>
                <Row>
                  <Radio.Group
                    className='cmt-radio'
                    onChange={(event) => setAddToReport(event.target.value)}
                    value={addToReport}
                  >
                    <Popover content={<TooltipContent />} placement="top">
                      <Radio value={true}>{t('examPage.uploadDocumentModal.radioYes')}</Radio>
                    </Popover>
                    <Radio value={false}>{t('examPage.uploadDocumentModal.radioNo')}</Radio>
                  </Radio.Group>
                </Row>
              </Col>
            </Row>
          </Col>
        )}
        <Col span={20}>
          <Row align="middle" justify="space-between" gutter={[16, 0]}>
            <Col span={12}>
              <Button
                className="cmt-button primary outline"
                type="primary"
                size="medium"
                block
                onClick={hasImage ? resetImage : closeModal}
              >
                {hasImage ? 'Borrar foto' : 'Cancelar'}
              </Button>
            </Col>
            <Col span={12}>
              <Button
                key={hasImage ? 'save' : 'capture'}
                type="primary"
                className={`cmt-button primary${hasImage && showAddToReport && addToReport === undefined ? ' disabled' : ''}`}
                size="medium"
                block
                onClick={capture}
                disabled={hasImage && showAddToReport && addToReport === undefined}
              >
                {hasImage ? 'Guardar foto' : 'Tomar foto'}
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </Modal>
  );
};

export default ExamWebcamModal;
